import React, { useEffect, useState } from "react";
import { Modal } from "@material-ui/core";
import { Bar } from "react-chartjs-2";
import { CircularProgress } from "@material-ui/core";
import { apiReports } from "../api/reports";

const blue = "#00acdc";
const green = "#94ba33";
const amber = "rgba(251,191,36)";

const pct = (v) => `${Math.round((v || 0) * 100)}%`;

const fmtDay = (iso) => {
  const d = new Date(iso + "T00:00:00Z");
  if (isNaN(d)) {return iso;}
  return `${d.getUTCDate()} ${d.toLocaleString("en", { month: "short", timeZone: "UTC" })}`;
};

// InspectorUtilisationDetailModal — drill-down for a single inspector row.
// Shows per-day completions (bar chart), weekday→area schedule, and completions
// split by area.
const InspectorUtilisationDetailModal = ({ open, onClose, userId, days, utilisation, capacity }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !userId) {return;}
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDetail(null);
    apiReports.getInspectorUtilisationDetail(userId, days)
      .then((res) => {
        if (cancelled) {return;}
        const r = res?.data?.result;
        if (res?.data?.status === 200 && r) {
          setDetail(r);
        } else {
          setError(res?.data?.message || "Failed to load detail.");
        }
        setLoading(false);
      })
      .catch(() => { if (!cancelled) { setError("Failed to load detail."); setLoading(false); } });
    return () => { cancelled = true; };
  }, [open, userId, days]);

  const perDay = detail?.perDay || [];
  const chartData = {
    labels: perDay.map((d) => fmtDay(d.date)),
    datasets: [{
      label: "Completions",
      data: perDay.map((d) => d.count),
      backgroundColor: blue,
      maxBarThickness: 18,
    }],
  };
  const chartOptions = {
    maintainAspectRatio: false,
    legend: { display: false },
    scales: {
      yAxes: [{ ticks: { beginAtZero: true, precision: 0 }, scaleLabel: { display: true, labelString: "Completions / day" } }],
      xAxes: [{ ticks: { autoSkip: true, maxRotation: 0, maxTicksLimit: 12 } }],
    },
    tooltips: { mode: "index", intersect: false },
  };

  const maxSplit = Math.max(1, ...(detail?.areaSplit || []).map((a) => a.completions));

  return (
    <Modal open={open} onClose={onClose}>
      <div style={{
        top: "50%", left: "50%", transform: "translate(-50%, -50%)",
        position: "absolute", maxWidth: 920, width: "92%",
        maxHeight: "88vh", overflowY: "auto",
      }} className="tw-bg-white tw-rounded-xl tw-shadow-lg tw-p-6 tw-outline-none">
        <div className="tw-flex tw-justify-between tw-items-start tw-mb-4">
          <div>
            <h2 className="tw-text-2xl tw-font-semibold">{detail?.name || "Inspector"}</h2>
            {detail && 
              <div className="tw-text-sm tw-text-gray-600 tw-mt-1">
                Default area: <b>{detail.defaultAreaName || "—"}</b>
                &nbsp;·&nbsp; Avg cap/day: <b>{detail.dailyCapacity}</b>
                &nbsp;·&nbsp; Last {detail.windowDays} days
              </div>
            }
          </div>
          <button onClick={onClose} className="tw-text-xl tw-px-2">✕</button>
        </div>

        {loading && <div className="tw-flex tw-justify-center tw-py-10"><CircularProgress /></div>}
        {error && <div className="tw-text-fs-red tw-py-6 tw-text-center">{error}</div>}

        {detail && 
          <>
            <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-5 tw-gap-4 tw-mb-6">
              <Stat label="Completions" value={detail.totalCompletions} color="tw-bg-primary" />
              <Stat label="Days worked" value={detail.daysWorked} color="tw-bg-fs-green" />
              <Stat label="Avg cap/day" value={detail.dailyCapacity} color="tw-bg-fs-green" />
              <Stat label="Period capacity" value={capacity || 0} color="tw-bg-gray-600" />
              <Stat label="Utilised" value={pct(utilisation)} color="tw-bg-fs-dark-blue" />
            </div>

            <div className="tw-bg-gray-50 tw-p-4 tw-rounded-lg tw-mb-6">
              <h3 className="tw-font-semibold tw-mb-2">Completions per day</h3>
              <div style={{ height: 220 }}>
                {perDay.some((d) => d.count > 0)
                  ? <Bar data={chartData} options={chartOptions} />
                  : <div className="tw-text-gray-500 tw-text-sm">No completions in this window.</div>}
              </div>
            </div>

            <div className="tw-grid md:tw-grid-cols-2 tw-gap-6">
              <div className="tw-bg-gray-50 tw-p-4 tw-rounded-lg">
                <h3 className="tw-font-semibold tw-mb-3">Weekly schedule</h3>
                <div className="tw-space-y-2">
                  {(detail.schedule || []).map((w) => 
                    <div key={w.weekday} className="tw-flex tw-items-center tw-justify-between tw-text-sm">
                      <span className="tw-w-12 tw-font-medium">{w.weekdayName}</span>
                      <span className={`tw-px-2 tw-py-0.5 tw-rounded tw-text-xs ${w.scheduled ? "tw-bg-green-100 tw-text-green-800" : "tw-bg-gray-200 tw-text-gray-500"}`}>
                        {w.scheduled ? "working" : "off"}
                      </span>
                      <span className="tw-flex-1 tw-text-right tw-truncate">
                        {w.workAreaName ? 
                          <span style={{ color: w.scheduled ? "#1f2937" : "#9ca3af" }}>{w.workAreaName}{w.capacity > 0 ? ` (${w.capacity})` : ""}</span>
                          : <span className="tw-text-gray-400">no area set</span>}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="tw-bg-gray-50 tw-p-4 tw-rounded-lg">
                <h3 className="tw-font-semibold tw-mb-3">Completions by area</h3>
                {(detail.areaSplit || []).length === 0
                  ? <p className="tw-text-gray-500 tw-text-sm">No completions to attribute in this window.</p>
                  : <div className="tw-space-y-2">
                    {detail.areaSplit.map((a) => 
                      <div key={a.workAreaId}>
                        <div className="tw-flex tw-justify-between tw-text-sm tw-mb-0.5">
                          <span className="tw-truncate">{a.workAreaName}</span>
                          <span className="tw-font-medium">{a.completions}</span>
                        </div>
                        <div className="tw-h-2 tw-rounded tw-bg-gray-200 tw-overflow-hidden">
                          <div className="tw-h-full" style={{ width: `${a.completions / maxSplit * 100}%`, background: green }} />
                        </div>
                      </div>
                    )}
                  </div>}
              </div>
            </div>
          </>
        }
      </div>
    </Modal>
  );
};

const Stat = ({ label, value, color }) => 
  <div className={`tw-rounded-lg tw-p-3 tw-text-white ${color}`}>
    <div className="tw-text-2xl tw-font-bold">{value}</div>
    <div className="tw-text-xs tw-uppercase tw-tracking-wider tw-opacity-90">{label}</div>
  </div>
;

export default InspectorUtilisationDetailModal;